import { useEffect, useState } from 'react';

/**
 * Renders a small build chip in the sidebar footer of both the admin and
 * portal SPAs. Reads <c>GET /version</c> once on mount; the endpoint is
 * anonymous so it works on the login screen too (we just don't render the
 * sidebar there).
 *
 * Shape of the response — see backend `VersionInfo`:
 * <pre>
 *   { "version": "0.1.0", "build": "142", "commit": "abc1234", "builtAt": "2026-05-20T18:00:00Z" }
 * </pre>
 *
 * Local dev builds return `build = "dev"` / `commit = "local"`; the chip
 * dims those so production reviewers can tell at a glance that they're
 * looking at a non-CI build.
 */
interface VersionInfo {
  version: string;
  build: string;
  commit: string;
  builtAt: string;
}

export function VersionBadge() {
  const [info, setInfo] = useState<VersionInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Plain fetch — no auth, no orval hook. Keeps the badge cheap and
    // working even when the user isn't signed in (e.g. on the login page
    // if we ever surface the badge there). Uses the `/api/` prefix that the
    // Vite dev proxy strips in dev and the backend's PathBase rewriter
    // strips in prod, so the same URL works both places.
    fetch('/api/version', { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json) setInfo(json as VersionInfo);
      })
      .catch(() => {
        // Silent — a missing /version is a docker-build issue, not a
        // user-facing one. The chip just stays hidden.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!info) return null;

  const isDev = info.build === 'dev' || info.commit === 'local';
  const buildLabel = isDev ? 'dev' : `build ${info.build}`;

  return (
    <div
      className={[
        'select-none border-t border-border/40 px-4 py-2 text-[10px] font-mono uppercase tracking-wider',
        isDev ? 'text-muted-foreground/60' : 'text-muted-foreground',
      ].join(' ')}
      title={info.builtAt && info.builtAt !== 'unknown' ? `Built ${info.builtAt}` : undefined}
    >
      v{info.version} · {buildLabel} · {info.commit}
    </div>
  );
}
